import { Skeleton } from '../ui';

/**
 * ProjectSkeleton: the project area while the project loads (CO-88, audit
 * SK1). It has the project's own shape — the header band with breadcrumb,
 * title, facts and actions, the section tabs, then summary tiles and two
 * panels — so nothing jumps when the data arrives.
 */
export function ProjectSkeleton() {
  return (
    <div className="project-area" role="status" aria-label="Loading project" data-testid="project-skeleton">
      <header className="project-top">
        {/* The breadcrumb's own box and line height, as for the title. */}
        <div className="project-top__breadcrumb breadcrumbs">
          <ol>
            <li>
              <Skeleton width="140px" height="1lh" className="skeleton--block" />
            </li>
          </ol>
        </div>
        <div className="project-top__row">
          <div className="project-top__identity">
            {/* The title in its own box, so its line height is the real one,
                beside the environment selector's place. */}
            <div className="project-top__name-row">
              <div className="project-top__title">
                <Skeleton width="220px" height="1lh" className="skeleton--block" />
              </div>
              <Skeleton width="150px" height="var(--control-height-md)" radius="var(--radius-md)" />
            </div>
            <ProjectFactsSkeleton />
          </div>
          <div className="project-top__actions">
            <Skeleton width="180px" height="32px" radius="var(--radius-md)" />
            <Skeleton width="76px" height="32px" radius="var(--radius-sm)" />
            <Skeleton width="32px" height="32px" radius="var(--radius-sm)" />
          </div>
        </div>
        <ProjectNavSkeleton />
      </header>
      <div className="project-body">
        <div className="project-skeleton__tiles">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} width="100%" height="104px" radius="var(--radius-md)" />
          ))}
        </div>
        <div className="project-skeleton__panels">
          <Skeleton width="100%" height="240px" radius="var(--radius-md)" />
          <Skeleton width="100%" height="240px" radius="var(--radius-md)" />
        </div>
      </div>
    </div>
  );
}

/**
 * ProjectFactsSkeleton holds the header's status-and-facts line, at its
 * real height, until the facts that fill it have been read once.
 */
export function ProjectFactsSkeleton() {
  return (
    <p className="project-top__facts" role="status" aria-label="Loading the environment's state" data-testid="project-facts-skeleton">
      <Skeleton width="104px" height="22px" radius="var(--radius-full)" />
      <Skeleton width="220px" height="14px" />
    </p>
  );
}

/**
 * ProjectNavSkeleton holds the section tabs' place until the facts that
 * decide which sections exist (platform, whether setup is finished) are
 * known, so a reload never shows one set of tabs and then another.
 */
export function ProjectNavSkeleton() {
  return (
    <div className="project-skeleton__nav" role="status" aria-label="Loading sections" data-testid="project-nav-skeleton">
      {[72, 96, 104, 132, 88, 136, 108, 72, 64].map((width, index) => (
        <Skeleton key={index} width={`${width}px`} height="14px" />
      ))}
    </div>
  );
}
